import { useEffect } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Stack, Box } from '@mui/material'
import {
  ObservationLogFormData,
  ObservationLogFormActions,
} from '@/components/forms'
import {
  ControlledTextField,
  ControlledDateTimeField,
  ControlledSelect,
  ControlledMediaUpload,
} from '@/components/inputs'
import { yupResolver } from '@hookform/resolvers/yup'
import { useAuth } from '@/hooks'
import { schema } from './validationSchema'
import { useApiErrorData } from '@/hooks/forms/useApiErrorData'
import { FormComponentProps, UserGroups } from '@/types'
import {
  BATS_BIRDS_COUNTING_METHOD_OPTIONS,
  OTHER_SPECIE_OPTION_VALUE,
  OTHER_GEDRAG_OPTION_VALUE,
  DIRECTION_BIRD_GEDRAG_OPTION_VALUE,
  BIRDS_BREEDING_SPECIE_OPTIONS,
  WIND_DIRECTION_OPTIONS,
  BIRDS_GEDRAG_OPTIONS,
  ALL_BIRDS_PLACE_FOUND_BEHAVIOUR,
  HOUSE_SPARROW_PLACE_FOUND_BEHAVIOUR,
  HOUSE_SPARROW_SPECIE,
  PLACE_FOUND_OPTION_VALUE,
  PLACE_NOT_FOUND_OPTION_VALUE,
} from '../fieldsOptions'

export const BirdsBreedingForm = ({
  onSubmit,
  defaultValues,
  isLoading,
  apiErrorData,
}: FormComponentProps<ObservationLogFormData>): JSX.Element => {
  const { t } = useTranslation('observationLogs')

  const { hasPermission } = useAuth()

  const methods = useForm<ObservationLogFormData>({
    mode: 'onChange',
    defaultValues: {
      specie: '',
      other_specie: '',
      amount: '',
      counting_method: '',
      behaviour: '',
      other_behaviour: '',
      behaviour_notes: '',
      direction: '',
      recording_number: '',
      is_place_found: undefined,
      address: '',
      observed_at: '',
      photos: undefined,
      sound_recordings: undefined,
    },
    values: defaultValues,
    resolver: yupResolver(schema),
  })

  useApiErrorData<ObservationLogFormData>(apiErrorData, methods)

  const watchedSpecie = methods.watch('specie')
  const watchedBehaviour = methods.watch('behaviour')

  useEffect(() => {
    const nestBehaviour =
      watchedSpecie === HOUSE_SPARROW_SPECIE
        ? HOUSE_SPARROW_PLACE_FOUND_BEHAVIOUR
        : ALL_BIRDS_PLACE_FOUND_BEHAVIOUR

    methods.setValue(
      'is_place_found',
      watchedBehaviour && nestBehaviour.includes(watchedBehaviour)
        ? PLACE_FOUND_OPTION_VALUE
        : PLACE_NOT_FOUND_OPTION_VALUE
    )
  }, [watchedSpecie, watchedBehaviour])

  const disabledForTheUser = !hasPermission([
    UserGroups.MANAGER,
    UserGroups.EMPLOYEE,
    UserGroups.DATA_CONTROLLER,
  ])

  return (
    <FormProvider {...methods}>
      <Stack mt={4} width="100%" alignItems="stretch" spacing={4}>
        <Stack direction="row" spacing={2} justifyContent="flex-start">
          <Box width="25%">
            <ControlledSelect
              control={methods.control}
              name="specie"
              label={t('observation-log.fieldLabels.specie')}
              options={BIRDS_BREEDING_SPECIE_OPTIONS}
              errorMessagePrefix=""
            />
          </Box>
          {methods.watch('specie') === OTHER_SPECIE_OPTION_VALUE && (
            <Box width="25%">
              <ControlledTextField
                control={methods.control}
                name="other_specie"
                placeholder={t('observation-log.fieldLabels.otherSpecie')}
                errorMessagePrefix=""
              />
            </Box>
          )}
          <Box width="25%">
            <ControlledTextField
              control={methods.control}
              name="amount"
              placeholder={t('observation-log.fieldLabels.amount')}
              type="number"
              disabled={disabledForTheUser}
              errorMessagePrefix=""
            />
          </Box>
          <Box width="25%">
            <ControlledSelect
              control={methods.control}
              name="counting_method"
              label={t('observation-log.fieldLabels.countingMethod')}
              options={BATS_BIRDS_COUNTING_METHOD_OPTIONS}
              disabled={disabledForTheUser}
              errorMessagePrefix=""
            />
          </Box>
          <Box width="25%">
            <ControlledTextField
              control={methods.control}
              name="recording_number"
              placeholder={t('observation-log.fieldLabels.recordingNumber')}
              disabled={disabledForTheUser}
              errorMessagePrefix=""
            />
          </Box>
        </Stack>
        <Stack direction="row" spacing={2} justifyContent="flex-start">
          <Box width="35%">
            <ControlledSelect
              control={methods.control}
              name="behaviour"
              label={t('observation-log.fieldLabels.behaviour')}
              options={BIRDS_GEDRAG_OPTIONS}
              errorMessagePrefix=""
            />
          </Box>
          {methods.watch('behaviour') === OTHER_GEDRAG_OPTION_VALUE && (
            <Box width="35%">
              <ControlledTextField
                control={methods.control}
                name="other_behaviour"
                placeholder={t('observation-log.fieldLabels.otherBehaviour')}
                errorMessagePrefix=""
              />
            </Box>
          )}
        </Stack>
        <ControlledTextField
          control={methods.control}
          name="behaviour_notes"
          placeholder={t('observation-log.fieldLabels.behaviourNotes')}
          multiline={true}
          rows={3}
          inputProps={{
            style: {
              padding: 0,
            },
          }}
          disabled={disabledForTheUser}
          errorMessagePrefix=""
        />
        <ControlledTextField
          name="address"
          control={methods.control}
          placeholder={t('observation-log.fieldLabels.address')}
          disabled={disabledForTheUser}
          errorMessagePrefix=""
        />
        {methods.watch('behaviour') === DIRECTION_BIRD_GEDRAG_OPTION_VALUE && (
          <>
            <ControlledSelect
              control={methods.control}
              name="direction"
              label={t('observation-log.fieldLabels.direction')}
              options={WIND_DIRECTION_OPTIONS}
              disabled={disabledForTheUser}
              errorMessagePrefix=""
            />
          </>
        )}
        <ControlledDateTimeField
          name="observed_at"
          control={methods.control}
          label={t('observation-log.fieldLabels.observedAt')}
          disabled={disabledForTheUser}
          errorMessagePrefix=""
        />
        <ControlledMediaUpload
          name="photos"
          control={methods.control}
          label={t('observation-log.fieldLabels.media')}
          disabled={disabledForTheUser}
          errorMessagePrefix=""
        />
        <ControlledMediaUpload
          name="sound_recordings"
          control={methods.control}
          accept="audio/wav,audio/x-wav"
          soundRecord={true}
          label={t('observation-log.fieldLabels.soundRecords', {
            maxNumber: 1,
          })}
          disabled={disabledForTheUser}
          errorMessagePrefix=""
        />
      </Stack>

      <ObservationLogFormActions
        onSubmit={onSubmit}
        defaultValues={defaultValues}
        isLoading={isLoading}
        methods={methods}
      />
    </FormProvider>
  )
}
